import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "../ui/command";
import {
	rankAndFilterExercises,
	getExerciseScopeBadgeMeta,
} from "../../lib/coachUtils";

/**
 * Searchable combobox used to re-link an already-added exercise (EMOM item,
 * complex step, superset step) to another library exercise.
 *
 * Replaces the plain Radix `<Select>` whose native typeahead showed no visible
 * search text ("la combo si sposta"). Options are expected pre-deduped by scope
 * (see `dedupeExercisesByScope`), so the list has one row per exercise with a
 * personal / global / P·G badge — consistent with every other exercise picker.
 */
export function ExerciseLinkCombobox({
	options,
	valueId,
	currentLabel,
	onSelect,
	onKeepCurrent,
	t,
	placeholder,
	keepCurrentLabel,
	triggerClassName = "h-7 text-[11px] md:text-[10px] bg-secondary border-transparent rounded-sm w-full justify-between font-normal",
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");

	const filtered = useMemo(
		() => rankAndFilterExercises(options || [], query, "label"),
		[options, query],
	);

	const selectedOption = (options || []).find(
		(option) => option.value === valueId,
	);
	const triggerText = selectedOption?.label || currentLabel || placeholder;

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setQuery("");
			}}
		>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={triggerClassName}
				>
					<span className="truncate">{triggerText}</span>
					<ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[260px] p-0 bg-popover border-border"
				align="start"
			>
				<Command shouldFilter={false}>
					<CommandInput
						value={query}
						onValueChange={setQuery}
						placeholder={placeholder}
						className="text-xs"
					/>
					<CommandList>
						<CommandEmpty className="py-4 text-xs text-muted-foreground">
							{t("noExercisesFound")}
						</CommandEmpty>
						{onKeepCurrent && (
							<CommandItem
								value="__keep_current__"
								onSelect={() => {
									onKeepCurrent();
									setOpen(false);
									setQuery("");
								}}
								className="text-xs text-muted-foreground"
							>
								{keepCurrentLabel || t("pbKeepCurrent")}
							</CommandItem>
						)}
						{filtered.map((option) => {
							const badge = getExerciseScopeBadgeMeta(option, t);
							return (
								<CommandItem
									key={option.value}
									value={option.value}
									onSelect={() => {
										onSelect(option);
										setOpen(false);
										setQuery("");
									}}
									className="text-xs"
								>
									<Check
										className={`mr-1 h-3 w-3 shrink-0 ${
											valueId === option.value ? "opacity-100" : "opacity-0"
										}`}
									/>
									<span className="truncate flex-1">{option.label}</span>
									<span
										className={`ml-1 text-[10px] px-1 py-0.5 rounded-sm uppercase tracking-wider ${badge.className}`}
									>
										{badge.label}
									</span>
								</CommandItem>
							);
						})}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
